import { type CSSProperties } from 'react';
import {
  qPatientPharmacyDispenseHistory,
  qPatientPharmacyQueue,
  qPatientPharmacySummary,
  type PatientPharmacyDispenseHistoryRow,
  type PatientPharmacySummaryRow,
  type PharmacyQueueRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../adapters/powersync';
import { NavIcon } from './shared';

// PRD.203 — Patient sidebar Pharmacy panel.
//
// Read-only summary of patient-wide Pharmacy activity, mirrored from the
// PRD.199 patient pharmacy queries. Renders the same surface in both the
// expanded sidebar body and the collapsed-rail right drawer. The "View
// All Pharmacy" CTA navigates to the full /patient/:id/pharmacy page,
// which is where dispense actions actually happen.

interface SidebarPharmacyPanelProps {
  patientId: string;
  /** Called when the user clicks "View All Pharmacy". */
  onViewAll: () => void;
}

const cardStyle: CSSProperties = {
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  padding: 'var(--sp-4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
};

const sectionHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const sectionTitleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text)',
};

export function SidebarPharmacyPanel({ patientId, onViewAll }: SidebarPharmacyPanelProps) {
  const { data: summaryRows = [] } = usePowerSyncQuery<PatientPharmacySummaryRow>(
    qPatientPharmacySummary(patientId),
  );
  const { data: historyRows = [] } = usePowerSyncQuery<PatientPharmacyDispenseHistoryRow>(
    qPatientPharmacyDispenseHistory(patientId),
  );
  const { data: queueRows = [] } = usePowerSyncQuery<PharmacyQueueRow>(
    qPatientPharmacyQueue(patientId),
  );
  const summary = summaryRows[0] ?? { ready_count: 0, partial_count: 0, dispensed_count: 0 };

  const recentHistory = historyRows.slice(0, 5);
  const readyItems = queueRows.slice(0, 6);

  return (
    <>
      {/* Header card with View All CTA */}
      <div style={cardStyle}>
        <div style={sectionHeaderStyle}>
          <span style={sectionTitleStyle}>
            <NavIcon name="clipboard" /> Pharmacy
          </span>
          <button
            onClick={onViewAll}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            View All <NavIcon name="chevron-right" />
          </button>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          Patient-wide medicines ready to dispense and dispense history.
        </p>

        {/* Summary chips */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 'var(--sp-2)',
          }}
        >
          <SummaryChip label="Ready" value={summary.ready_count ?? 0} tone="info" />
          <SummaryChip label="Partial" value={summary.partial_count ?? 0} tone="warning" />
          <SummaryChip label="Dispensed" value={summary.dispensed_count ?? 0} tone="success" />
        </div>
      </div>

      {/* Ready to dispense — pending Medicine lines on paid invoices (includes
          package redemptions). Confirming/batch-picking happens on the full
          Pharmacy page via "View All Pharmacy". */}
      {readyItems.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <span style={sectionTitleStyle}>
              <NavIcon name="clipboard" /> Ready to Dispense
            </span>
            <button
              onClick={onViewAll}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-primary)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              Dispense <NavIcon name="chevron-right" />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {readyItems.map((row, i) => (
              <div
                key={row.invoice_item_id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  padding: 'var(--sp-2) 0',
                  borderBottom: i < readyItems.length - 1 ? '1px solid var(--border)' : 'none',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-2)' }}
                >
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.item_name ?? row.description_snapshot ?? 'Medicine'}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: 'var(--color-info-text, #1e40af)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.quantity_remaining ?? 0} {row.unit_snapshot ?? row.inventory_unit ?? ''}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  Ready
                  {row.invoice_no ? ` · ${row.invoice_no}` : ''}
                  {row.invoice_date ? ` · ${row.invoice_date}` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent dispensed history */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>
          <NavIcon name="check-circle" /> Recent Dispenses
        </div>
        {recentHistory.length === 0 ? (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              padding: 'var(--sp-2) 0',
            }}
          >
            No dispenses recorded for this patient yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {recentHistory.map((row, i) => (
              <div
                key={row.pharmacy_dispense_line_id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  padding: 'var(--sp-2) 0',
                  borderBottom: i < recentHistory.length - 1 ? '1px solid var(--border)' : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 'var(--sp-2)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.item_name_snapshot ?? 'Medicine'}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.quantity_dispensed ?? 0} {row.unit_snapshot ?? ''}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {row.dispensed_at ? row.dispensed_at.replace('T', ' ').slice(0, 16) : '—'}
                  {row.batch_number_snapshot ? ` · Batch ${row.batch_number_snapshot}` : ''}
                  {row.invoice_no ? ` · ${row.invoice_no}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <button
        onClick={onViewAll}
        style={{
          height: 36,
          padding: '0 14px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-primary)',
          background: 'var(--bg-surface)',
          color: 'var(--color-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <NavIcon name="clipboard" /> View All Pharmacy
      </button>
    </>
  );
}

function SummaryChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'info' | 'warning' | 'success';
}) {
  const palette = {
    info: { bg: 'var(--color-info-bg, #dbeafe)', color: 'var(--color-info-text, #1e40af)' },
    warning: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
    success: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  }[tone];
  return (
    <div
      style={{
        padding: 'var(--sp-2) var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        background: palette.bg,
        color: palette.color,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        textAlign: 'center',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-bold, 700)',
          lineHeight: 1,
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 'var(--font-weight-semi)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          opacity: 0.85,
        }}
      >
        {label}
      </span>
    </div>
  );
}
